import { useState } from 'react';
import { TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { KnowledgeDocument } from '../../../hooks/useKnowledgeDocuments';
import KnowledgeStatusBadge from './KnowledgeStatusBadge';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface KnowledgeDocumentListProps {
  items: KnowledgeDocument[];
  loading?: boolean;
  onDelete?: (id: string) => void;
  /** Inherited (station) docs shown on a device page: read-only, dimmer. */
  inherited?: boolean;
  /** Scope title, used to make the empty state specific. */
  emptyHint?: string;
}

export default function KnowledgeDocumentList({
  items,
  loading = false,
  onDelete,
  inherited = false,
  emptyHint,
}: KnowledgeDocumentListProps) {
  const { t } = useTranslation();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  if (loading && items.length === 0) {
    return (
      <p className="text-xs text-app-text-dim">
        {t('knowledge.loading', { defaultValue: 'กำลังโหลดเอกสาร…' })}
      </p>
    );
  }

  if (items.length === 0) {
    if (inherited) return null;
    return (
      <div className="rounded-lg border border-dashed border-app-border/60 bg-app-muted/10 px-3 py-4 text-center">
        <p className="text-xs text-app-text-dim">
          {emptyHint
            ? t('knowledge.emptyScoped', {
                defaultValue: 'ยังไม่มีเอกสารสำหรับ {{scope}}',
                scope: emptyHint,
              })
            : t('knowledge.empty', { defaultValue: 'ยังไม่มีเอกสาร knowledge' })}
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-1.5">
      {items.map((doc) => {
        const confirming = confirmId === doc.id;
        return (
          <li
            key={doc.id}
            className={`group flex items-start gap-3 rounded-md px-2 py-1.5 transition-colors ${
              inherited ? 'bg-transparent opacity-75' : 'hover:bg-app-muted/10'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-xs font-medium text-ink" title={doc.filename}>
                  {doc.filename}
                </p>
                <KnowledgeStatusBadge status={doc.status} />
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] tabular-nums text-app-text-dim">
                  {formatBytes(doc.size_bytes)}
                </span>
                {doc.uploaded_by ? (
                  <span
                    className="max-w-[10rem] truncate text-[10px] text-app-text-dim"
                    title={doc.uploaded_by}
                  >
                    · {doc.uploaded_by}
                  </span>
                ) : null}
              </div>
              {doc.status === 'failed' && doc.error ? (
                <p className="mt-1 flex items-start gap-1 text-[10px] text-semantic-down">
                  <ExclamationTriangleIcon className="mt-px h-3 w-3 shrink-0" />
                  <span className="line-clamp-2">{doc.error}</span>
                </p>
              ) : null}
            </div>

            {onDelete && !inherited ? (
              confirming ? (
                <span className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      onDelete(doc.id);
                      setConfirmId(null);
                    }}
                    className="rounded bg-semantic-down/15 px-2 py-1 text-[10px] font-bold text-semantic-down hover:bg-semantic-down/25"
                  >
                    {t('knowledge.confirmDelete', { defaultValue: 'ยืนยันลบ' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    className="rounded px-2 py-1 text-[10px] text-app-text-dim hover:bg-app-muted/40"
                  >
                    {t('common.cancel', { defaultValue: 'ยกเลิก' })}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmId(doc.id)}
                  className="shrink-0 rounded p-1.5 text-app-text-dim opacity-40 transition-opacity hover:bg-semantic-down/10 hover:text-semantic-down group-hover:opacity-100"
                  title={t('knowledge.delete', { defaultValue: 'ลบ' })}
                  aria-label={t('knowledge.delete', { defaultValue: 'ลบ' })}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
